import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon, QuestionMarkCircleIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Link } from 'react-router-dom';

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "L'application est-elle sécurisée ?",
      answer: "Oui. VADOM utilise des protocoles de chiffrement avancés pour protéger vos données. Toutes les transactions financières passent par des passerelles sécurisées certifiées (PCI-DSS). Vos informations personnelles ne sont jamais partagées.",
    },
    {
      question: "Comment vérifiez-vous les prestataires (Smarts) ?",
      answer: "Chaque Smart passe par un processus de vérification rigoureux : vérification d'identité (pièce d'identité + selfie), validation des compétences et vérification des références. Seuls les prestataires validés reçoivent le badge 'Vérifié'.",
    },
    {
      question: "Quels sont les avantages pour devenir Smart ?",
      answer: "En tant que Smart, vous bénéficiez de : visibilité auprès de milliers de clients, gestion d'agenda intégrée, paiements 100% sécurisés, tableau de bord analytique, support prioritaire et la possibilité de développer votre activité sans frais cachés.",
    },
    {
      question: "L'application est-elle disponible en dehors du Togo ?",
      answer: "Actuellement, VADOM est disponible à Lomé (Togo). Notre expansion est en cours vers d'autres villes du Togo et pays d'Afrique de l'Ouest (Bénin, Côte d'Ivoire, Ghana). Restez connectés !",
    },
    {
      question: "Comment se passe le paiement ?",
      answer: "Le paiement est sécurisé et s'effectue via mobile money (T Money, Flooz), carte bancaire ou espèces. Pour les prestations, le montant est pré-autorisé avant l'intervention et débloqué après validation du client.",
    },
    {
      question: "Puis-je annuler une réservation ?",
      answer: "Oui, vous pouvez annuler gratuitement jusqu'à 24h avant la prestation. En cas d'annulation tardive, des frais peuvent s'appliquer. Consultez nos conditions d'utilisation pour plus de détails.",
    },
    {
      question: "Comment devenir Smart VADOM ?",
      answer: "Remplissez le formulaire sur la page 'Devenir Smart', notre équipe vérifie votre profil sous 48h ouvrées. Une fois validé, vous configurez votre profil, vos tarifs et vos disponibilités. C'est gratuit !",
    },
    {
      question: "Quels types de services puis-je trouver sur VADOM ?",
      answer: "VADOM couvre de nombreuses catégories : artisanat (plomberie, électricité, menuiserie), services à domicile (ménage, jardinage), services numériques (design, développement web), beauté (coiffure, soins), transport (livraison, déménagement) et formation.",
    },
    {
      question: "Comment sont fixés les tarifs ?",
      answer: "Chaque Smart fixe librement son tarif horaire. Vous voyez le prix avant de réserver. VADOM prélève une commission sur chaque transaction, transparente pour le client.",
    },
    {
      question: "Que faire si je ne suis pas satisfait du service ?",
      answer: "Votre satisfaction est notre priorité. Contactez notre support sous 24h après la prestation. Nous analysons la situation et trouvons une solution : retour du prestataire, remboursement partiel ou total selon les cas.",
    },
  ];

  return (
    <div className="overflow-hidden bg-[#0a0a0a] min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#252525] to-[#374151]/30 text-white py-16 lg:py-20 overflow-hidden">
        
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6"
            >
              <QuestionMarkCircleIcon className="h-5 w-5" />
              <span className="text-sm font-medium">Foire Aux Questions</span>
            </motion.div>
            
            <h1 className="text-4xl lg:text-5xl font-display font-bold mb-4">
              Comment pouvons-nous <span className="text-white/90">vous aider ?</span>
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Tout ce que vous devez savoir sur VADOM
            </p>
          </motion.div>
        </div>
        
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0 120L60 105C120 90 240 60 360 45C480 30 600 30 720 37.5C840 45 960 60 1080 67.5C1200 75 1320 75 1380 75L1440 75V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z" 
                  fill="white"/>
          </svg>
        </div>
      </section>

      <div className="container-custom py-12 lg:py-16 max-w-4xl">  
        {/* FAQ Accordion */}
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.03 }}
              className={`bg-[#111111] rounded-2xl border transition-all duration-300 cursor-pointer
                ${openIndex === index ? 'border-[#F97316]' : 'border-[#252525] hover:border-[#F97316]/30'}`}
              onClick={() => setOpenIndex(openIndex === index ? null : index)}
            >
              <div className="flex items-center justify-between p-6">
                <h3 className="font-semibold text-white pr-4 text-lg">
                  {faq.question}
                </h3>
                <ChevronDownIcon 
                  className={`h-5 w-5 text-[#F97316] flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`} 
                />
              </div>
              
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6">
                      <div className="h-px bg-gradient-to-r from-transparent via-[#252525] to-transparent mb-4" />
                      <p className="text-[#AAAAAA] leading-relaxed">
                        {faq.answer}
                      </p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-12 text-center"
        >
          <div className="bg-gradient-to-br from-[#111111] to-[#0a0a0a] rounded-2xl p-8 border border-[#252525]">
            <ChatBubbleLeftRightIcon className="h-12 w-12 text-[#F97316] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">
              Vous n'avez pas trouvé votre réponse ?
            </h3>
            <p className="text-[#AAAAAA] mb-6">
              Notre équipe support est disponible 7j/7 pour vous aider
            </p>
            <Link to="/contact" className="inline-block bg-[#F97316] text-white px-8 py-3 rounded-xl font-semibold hover:bg-[#e8630a] transition-all">
              Nous contacter
            </Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default FaqPage;